import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ignoreSoftware,
  promoteSoftware,
  saveSoftwarePolicy,
  scanSoftware,
  softwareShellQuery,
  type SoftwareRow,
} from '../../api/admSoftware'
import { Content } from '../../ui/Shell'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { shellQuery } from '../../api/shell'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, AdmField } from './AdmFilterBar'
import { SettingsCheck } from '../../ui/SettingsParts'

/**
 * ADM -> Software queue, din `views/version-3/softsee/adm/software/index.php`.
 *
 * DE CE EXISTA ECRANUL, si nu o scriere directa in CMDB: regula de semnificatie
 * e noua. Un program promovat gresit nu se sterge curat - pana atunci s-a legat
 * de alte fise si de tichete - iar o coada gresita se goleste dintr-un clic.
 *
 * Lista NU e un DataTable: in portal randurile veneau randate pe server, cel
 * mult doua sute, fara paginare si fara sortare. Ramane la fel - un tabel care
 * ar fi capatat aici paginare ar fi cerut si un al doilea capat de API pentru
 * ceva ce incape intr-un raspuns.
 *
 * Cele doua avertismente de deasupra - tipul de relatie lipsa si catalogul
 * schimbat - stau AICI, unde se vad urmarile, nu intr-un ecran de setari.
 */

const STYLES = ['css/adm.css']

interface ScanTotals {
  queued: number
  promoted: number
  unlinked: number
  relinked: number
}

/** Ce a facut scanarea, pe toate pumnii adunati. */
function scanSummary(t: ScanTotals): string {
  const parts = [`Scan finished: ${t.queued} new candidate(s).`]
  if (t.promoted > 0) {
    parts.push(
      `${t.promoted} added to the CMDB automatically - they left the waiting list; pick "Promoted" to see them.`,
    )
  }
  if (t.unlinked > 0) parts.push(`${t.unlinked} could not be linked to their machine.`)
  if (t.relinked > 0) parts.push(`${t.relinked} earlier item(s) linked to their machine.`)
  return parts.join(' ')
}

export function SoftwarePendingPage() {
  const chrome = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, chrome.data?.version ?? '')

  const [state, setState] = useState(1)
  const shell = useQuery(softwareShellQuery(state))

  const [scanning, setScanning] = useState(false)
  const [scanNote, setScanNote] = useState('')

  const rows = shell.data?.rows ?? []
  const canWrite = shell.data?.canWrite ?? false
  const waiting = shell.data?.waiting ?? 0
  const drift = shell.data?.drift
  const companyId = shell.data?.filters.company_id ?? 0

  const decide = async (row: SoftwareRow, what: (id: number) => Promise<{ status: string; message: string }>) => {
    try {
      const answer = await what(row.ID)
      toast(answer.message, 'Software queue', answer.status === 'OK' ? 'success' : 'error')
      if (answer.status === 'OK') void shell.refetch()
    } catch (error) {
      toast((error as Error).message, 'Software queue', 'error')
    }
  }

  const setPolicy = async (on: boolean) => {
    try {
      const answer = await saveSoftwarePolicy(on)
      toast(answer.message, 'Software queue', answer.status === 'OK' ? 'success' : 'error')
      void shell.refetch()
    } catch (error) {
      toast((error as Error).message, 'Software queue', 'error')
    }
  }

  /*
   * Scanarea merge pe pumni de masini, si butonul o duce singur pana la capat.
   * Cursorul vine inapoi de la server: o cerere care cade lasa ecranul spunand
   * cat s-a facut, nu o bara care se invarte la nesfarsit.
   */
  const scanFrom = async (offset: number, sum: ScanTotals = { queued: 0, promoted: 0, unlinked: 0, relinked: 0 }) => {
    try {
      const answer = await scanSoftware(companyId, offset)

      if (answer.status !== 'OK') {
        setScanning(false)
        toast(answer.message, 'Software queue', 'error')
        return
      }

      const totals: ScanTotals = {
        queued: sum.queued + (answer.queued ?? 0),
        promoted: sum.promoted + (answer.promoted ?? 0),
        unlinked: sum.unlinked + (answer.unlinked ?? 0),
        relinked: sum.relinked + (answer.relinked ?? 0),
      }

      setScanNote(`${offset + answer.scanned} of ${answer.total} machines looked at`)

      if (answer.next_offset > 0) {
        await scanFrom(answer.next_offset, totals)
        return
      }

      setScanning(false)
      /* Spune CE s-a facut. Cu promovarea automata pornita, programele a caror
         masina are fisa trec singure in CMDB si ies din lista de asteptare -
         fara numaratoarea asta, lista golita parea pierduta. */
      setScanNote(scanSummary(totals))
      toast(scanSummary(totals), 'Software queue', 'success')
      void shell.refetch()
    } catch (error) {
      setScanning(false)
      toast((error as Error).message, 'Software queue', 'error')
    }
  }

  const startScan = () => {
    if (scanning) return
    setScanning(true)
    setScanNote('starting…')
    void scanFrom(0)
  }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="yellow"
          icon="fa-cube"
          title="Software waiting for a decision"
          note="installed software the platform considers operationally significant. Everything else stays in the inventory, where it belongs"
          stats={[{ value: waiting.toLocaleString('en-US'), label: 'waiting', tone: waiting > 0 ? 'yellow' : 'grey' }]}
          actions={
            canWrite ? (
              <>
                <button type="button" className="btn btn-sm btn-info" id="admSoftwareScan" disabled={scanning} onClick={startScan}>
                  <i className="fas fa-search"></i> Scan this customer
                </button>
                <div className="adm-muted" id="admSoftwareScanNote" style={{ marginTop: 6 }}>
                  {scanNote}
                </div>
              </>
            ) : undefined
          }
        />

        <AdmFilterBar>
          <AdmField label="Decision" htmlFor="admSoftwareState">
            <select className="form-control input-sm" id="admSoftwareState" value={state} onChange={(e) => setState(Number(e.target.value))}>
              {(shell.data?.states ?? []).map((one) => (
                <option key={one.value} value={one.value}>
                  {one.label}
                </option>
              ))}
            </select>
          </AdmField>
        </AdmFilterBar>

        <div className="adm-card adm-tone-yellow" style={{ marginBottom: 16 }}>
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-layer-group"></i> Three tiers, not two
            </h5>
            <span className="adm-card-note">why so little arrives here</span>
          </div>

          <p className="adm-muted" style={{ margin: '0 0 8px' }}>
            Every installed program is <strong>Detected</strong> &mdash; the inventory keeps all of it. A program the
            platform tracks is <strong>Managed</strong>: it carries a license, it has known vulnerabilities, or it runs as
            a service. Only the top tier, <strong>Configuration item</strong>, is offered here, and only that tier reaches
            the CMDB.
          </p>
          <p className="adm-muted" style={{ margin: 0 }}>
            The middle tier is not stored anywhere. It is worked out when a screen asks, from the catalogue and from what
            the machine is doing &mdash; so changing the rule changes every answer at once, instead of leaving yesterday's
            verdicts behind in a table.
          </p>
        </div>

        {/*
          Lipsa tipului de relatie se spune AICI, unde se vad urmarile. Nu se
          inventeaza un tip de relatie din ecran - el poarta si steagul de
          propagare a impactului, si regulile de perechi de clase - dar nici nu
          se tace.
        */}
        {shell.data && !shell.data.canLink ? (
          <div className="adm-card adm-tone-red" style={{ marginBottom: 16 }} id="admNoRelationType">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-unlink"></i> Software items will land unlinked
              </h5>
              <span className="adm-card-note">one row is missing from your CMDB vocabulary</span>
            </div>
            <p className="adm-muted" style={{ margin: 0 }}>
              This provider has no <code>runs_on</code> relation type, so a software item cannot be attached to the
              machine it runs on. It will still be created &mdash; but impact analysis will not see it, which is worse
              than not having it at all. Add the relation type before turning automatic promotion loose.
            </p>
          </div>
        ) : null}

        <div className="adm-card adm-tone-yellow" style={{ marginBottom: 16 }}>
          <div className="adm-card-head">
            <h5>
              <i className="fas fa-robot"></i> What goes in on its own
            </h5>
            <span className="adm-card-note">and where you can teach the platform your own vocabulary</span>
          </div>

          <div className="adm-policy">
            <SettingsCheck
              className="admSoftwarePolicy"
              id="autoPromoteSoftware"
              checked={shell.data?.policy ?? false}
              disabled={!canWrite || !shell.data}
              onChange={(on) => void setPolicy(on)}
              label={
                <>
                  <strong>Add significant software automatically.</strong> Software that reaches the top tier is added to
                  the CMDB and linked to the machine it runs on, without waiting here. <em>Except</em> when the machine
                  itself is not a configuration item yet &mdash; a software item with nothing to hang from cannot answer
                  &ldquo;what breaks if this goes&rdquo;, and those still wait below.
                </>
              }
            />
          </div>

          <p className="adm-muted" style={{ margin: '10px 0 0' }}>
            <i className="fas fa-tags"></i> Applications are placed by name unless you say otherwise.{' '}
            {/* Anchor, nu `Link`: e o trecere intre doua ecrane, o data la cateva luni,
                si asa arata si in portal. Un `Link` ar fi cerut contextul routerului
                pana si acolo unde ecranul se deseneaza singur, la proba. */}
            <a href="/app/adm/softwareCategories">Map your own categories</a> and yours will be used first.
          </p>
        </div>

        {/*
          Cand baza s-a departat de catalogul din cod, se spune AICI - un ecran
          de setari ar fi ascuns exact legatura care conteaza: de ce lista de mai
          jos arata altfel decat se astepta omul.
        */}
        {drift && (drift.edited.length > 0 || drift.missing.length > 0) ? (
          <div className="adm-card adm-tone-blue-hoki" style={{ marginBottom: 16 }} id="admCatalogDrift">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-code-branch"></i> Your catalogue differs from the shipped one
              </h5>
              <span className="adm-card-note">this is not a fault &mdash; it is what you changed</span>
            </div>
            {drift.edited.length > 0 ? (
              <p className="adm-muted" style={{ margin: '0 0 6px' }}>
                Edited here: <strong>{drift.edited.join(', ')}</strong>
              </p>
            ) : null}
            {drift.missing.length > 0 ? (
              <p className="adm-muted" style={{ margin: 0 }}>
                Removed here: <strong>{drift.missing.join(', ')}</strong>
              </p>
            ) : null}
          </div>
        ) : null}

        <table className="table table-hover adm-list" id="admSoftwareTable" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Software</th>
              <th style={{ width: 110 }}>Version</th>
              <th style={{ width: 150 }}>Category</th>
              <th>Machine</th>
              <th>Customer</th>
              <th>Why it is here</th>
              <th style={{ width: 150 }}>Found</th>
              {canWrite && state === 1 ? <th style={{ width: 190 }}>Decide</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="adm-muted" style={{ padding: 22, textAlign: 'center' }}>
                  Nothing is waiting. Run a scan on a machine to see what the rule would offer.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={row.ID} data-id={row.ID}>
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td>{row.version}</td>
                <td>{row.category}</td>
                <td>{row.device_name}</td>
                <td>{row.company_name}</td>
                <td className="adm-muted">{row.reasons}</td>
                <td className="adm-muted">{row.detected_at}</td>
                {canWrite && state === 1 ? (
                  <td>
                    <button
                      type="button"
                      className="btn btn-xs btn-info admSoftwarePromote"
                      onClick={() => void decide(row, promoteSoftware)}
                    >
                      <i className="fas fa-plus"></i> Add to CMDB
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn-xs btn-default admSoftwareIgnore"
                      onClick={() => void decide(row, ignoreSoftware)}
                    >
                      Never
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Content>
  )
}
